import {
  Alert,
  Card,
  Col,
  Button,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Spin,
  Switch,
  Tag,
  theme,
  Typography,
  message,
} from "antd";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { CorporationOperation } from "../../../serviceoperation/CorporationOperation";
import { CompanyContext } from "../../../contexts/company-context";
import dayjs from "dayjs";
import {
  PolicyResponse,
  PolicyRule,
} from "../../../serviceoperation/entity/CorporationOperationEntity";
import { CircleDollarSign, Compass, IdCard, ListChecks } from "lucide-react";

interface CorpMetadataFormProps {
  newCorpData?: unknown[];
}

type PrimitiveValue = boolean | number | string | null;
type DirtyState = Record<string, Record<string, PrimitiveValue>>;

const POLICY_LABELS: Record<string, string> = {
  clock_activity: "Clock Activity",
  geofencing: "Geofencing",
  payroll: "Payroll",
  schedule_self_service: "Schedule Self Service",
};

const POLICY_ORDER = [
  "clock_activity",
  "geofencing",
  "payroll",
  "schedule_self_service",
];

const POLICY_ICONS: Record<string, React.ReactNode> = {
  clock_activity: <ListChecks size={16} />,
  geofencing: <Compass size={16} />,
  payroll: <CircleDollarSign size={16} />,
  schedule_self_service: <IdCard size={16} />,
};

const CorpMetadataForm: React.FC<CorpMetadataFormProps> = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [policies, setPolicies] = useState<PolicyResponse[]>([]);
  const [dirtyValuesByPolicy, setDirtyValuesByPolicy] = useState<DirtyState>(
    {},
  );
  const [savingPolicyKey, setSavingPolicyKey] = useState<string>("");
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const { selectedCompany } = useContext(CompanyContext);
  const { token } = theme.useToken();
  const corporationOps = new CorporationOperation();

  const getOrderedPolicies = (input: PolicyResponse[]): PolicyResponse[] => {
    const ranking = new Map(POLICY_ORDER.map((key, index) => [key, index]));
    return [...input].sort((left, right) => {
      const leftRank = ranking.get(left.policyKey) ?? Number.MAX_SAFE_INTEGER;
      const rightRank = ranking.get(right.policyKey) ?? Number.MAX_SAFE_INTEGER;
      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }
      return left.policyKey.localeCompare(right.policyKey);
    });
  };

  const loadPolicies = async () => {
    if (!selectedCompany?.corporationId) {
      return;
    }

    setLoading(true);
    try {
      const result = await corporationOps.getCorpPoliciesAsync(
        selectedCompany.corporationId,
      );

      if (result.statusCode === 403) {
        setIsReadOnly(true);
        message.warning(
          "You are not authorized to update organization policies.",
        );
      }

      if (result.statusCode !== 200 || !result.data) {
        message.error(
          result.statusMessage || "Failed to load organization policies.",
        );
        setPolicies([]);
        return;
      }

      const nextPolicies = Array.isArray(result.data)
        ? result.data
        : Object.values(result.data as Record<string, PolicyResponse>);

      setPolicies(getOrderedPolicies(nextPolicies));
      setDirtyValuesByPolicy({});
    } catch (error) {
      console.error("Error loading corporation policies", error);
      message.error("Failed to load organization policies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, [selectedCompany]);

  const policyStats = useMemo(() => {
    const sectionCount = policies.length;
    const fieldCount = policies.reduce(
      (sum, section) =>
        sum +
        new Set([
          ...Object.keys(section.values ?? {}),
          ...Object.keys(section.rules ?? {}),
        ]).size,
      0,
    );
    const dirtySectionCount = Object.keys(dirtyValuesByPolicy).length;
    const dirtyFieldCount = Object.values(dirtyValuesByPolicy).reduce(
      (sum, values) => sum + Object.keys(values).length,
      0,
    );
    return {
      sectionCount,
      fieldCount,
      dirtySectionCount,
      dirtyFieldCount,
    };
  }, [dirtyValuesByPolicy, policies]);

  const formatFieldLabel = (fieldKey: string) => {
    const spaced = fieldKey
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  const inferRuleFromValue = (
    value: PrimitiveValue,
    fieldKey: string,
  ): PolicyRule => {
    if (typeof value === "boolean") {
      return {
        fieldType: "Boolean",
        description: `${formatFieldLabel(fieldKey)} (metadata unavailable)`,
        modifiable: false,
        min: null,
        max: null,
      };
    }

    if (typeof value === "number") {
      return {
        fieldType: "Integer",
        description: `${formatFieldLabel(fieldKey)} (metadata unavailable)`,
        modifiable: false,
        min: null,
        max: null,
      };
    }

    const parsedDate =
      typeof value === "string" && value.length > 0 ? dayjs(value) : null;
    return {
      fieldType: parsedDate && parsedDate.isValid() ? "DateTime" : "String",
      description: `${formatFieldLabel(fieldKey)} (metadata unavailable)`,
      modifiable: false,
      min: null,
      max: null,
    };
  };

  const normalizeFieldKey = (fieldKey: string) =>
    fieldKey.replace(/[_\s-]/g, "").toLowerCase();

  const getLookupByNormalizedKey = (obj: Record<string, unknown>) => {
    const lookup = new Map<string, string>();
    Object.keys(obj ?? {}).forEach((key) => {
      const normalizedKey = normalizeFieldKey(key);
      if (!lookup.has(normalizedKey)) {
        lookup.set(normalizedKey, key);
      }
    });
    return lookup;
  };

  const getRenderableFieldKeys = (policy: PolicyResponse) => {
    const valueKeys = Object.keys(policy.values ?? {});
    const ruleKeys = Object.keys(policy.rules ?? {});

    const canonicalByNormalized = new Map<string, string>();

    valueKeys.forEach((key) => {
      canonicalByNormalized.set(normalizeFieldKey(key), key);
    });

    ruleKeys.forEach((key) => {
      const normalizedKey = normalizeFieldKey(key);
      if (!canonicalByNormalized.has(normalizedKey)) {
        canonicalByNormalized.set(normalizedKey, key);
      }
    });

    return Array.from(canonicalByNormalized.values());
  };

  const resolveRuleForField = (policy: PolicyResponse, fieldKey: string) => {
    const directRule = policy.rules?.[fieldKey];
    if (directRule) {
      return directRule;
    }

    const normalizedKey = normalizeFieldKey(fieldKey);
    const ruleLookup = getLookupByNormalizedKey(policy.rules ?? {});
    const matchedRuleKey = ruleLookup.get(normalizedKey);
    if (matchedRuleKey) {
      return policy.rules[matchedRuleKey];
    }

    return inferRuleFromValue(policy.values[fieldKey] ?? null, fieldKey);
  };

  const resolveValueForField = (policy: PolicyResponse, fieldKey: string) => {
    if (Object.prototype.hasOwnProperty.call(policy.values ?? {}, fieldKey)) {
      return getCurrentValue(policy, fieldKey);
    }

    const normalizedKey = normalizeFieldKey(fieldKey);
    const valueLookup = getLookupByNormalizedKey(policy.values ?? {});
    const matchedValueKey = valueLookup.get(normalizedKey);
    if (matchedValueKey) {
      return getCurrentValue(policy, matchedValueKey);
    }

    return null;
  };

  const parseDateValue = (value: PrimitiveValue) => {
    if (typeof value !== "string" || value.length === 0) {
      return null;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  };

  const getCurrentValue = (
    policy: PolicyResponse,
    fieldKey: string,
  ): PrimitiveValue => {
    const dirtyValue = dirtyValuesByPolicy[policy.policyKey]?.[fieldKey];
    if (dirtyValue !== undefined) {
      return dirtyValue;
    }
    return policy.values[fieldKey] ?? null;
  };

  const updateDirtyValue = (
    policy: PolicyResponse,
    fieldKey: string,
    nextValue: PrimitiveValue,
  ) => {
    const originalValue = policy.values[fieldKey] ?? null;
    const normalizedOriginal = originalValue ?? null;
    const normalizedNext = nextValue ?? null;
    const isChanged = normalizedOriginal !== normalizedNext;

    setDirtyValuesByPolicy((prev) => {
      const currentPolicyDirty = { ...(prev[policy.policyKey] ?? {}) };

      if (isChanged) {
        currentPolicyDirty[fieldKey] = normalizedNext;
      } else {
        delete currentPolicyDirty[fieldKey];
      }

      const next = { ...prev };
      if (Object.keys(currentPolicyDirty).length > 0) {
        next[policy.policyKey] = currentPolicyDirty;
      } else {
        delete next[policy.policyKey];
      }

      return next;
    });
  };

  const savePolicySection = async (policyKey: string) => {
    if (!selectedCompany?.corporationId) {
      return;
    }

    const changedValues = dirtyValuesByPolicy[policyKey];
    if (!changedValues || Object.keys(changedValues).length === 0) {
      message.info("No changes to save.");
      return;
    }

    setSavingPolicyKey(policyKey);
    try {
      const response = await corporationOps.patchCorpPolicySectionAsync(
        selectedCompany.corporationId,
        policyKey,
        { values: changedValues },
      );

      if (response.statusCode !== 200 || !response.data) {
        message.error(
          response.statusMessage || "Unable to save policy updates.",
        );
        return;
      }

      const updatedSection = response.data;

      message.success("Policy settings saved.");
      setPolicies((prev) =>
        prev.map((section) =>
          section.policyKey === policyKey ? updatedSection : section,
        ),
      );
      setDirtyValuesByPolicy((prev) => {
        const next = { ...prev };
        delete next[policyKey];
        return next;
      });
    } catch (error) {
      console.error("Error saving policy section", error);
      message.error("Unable to save policy updates.");
    } finally {
      setSavingPolicyKey("");
    }
  };

  const renderField = (
    policy: PolicyResponse,
    fieldKey: string,
    rule: PolicyRule,
  ) => {
    const value = resolveValueForField(policy, fieldKey);
    const disabled = isReadOnly || !rule.modifiable;

    switch (rule.fieldType) {
      case "Boolean":
        return (
          <Switch
            checked={Boolean(value)}
            disabled={disabled}
            checkedChildren="On"
            unCheckedChildren="Off"
            onChange={(checked) => updateDirtyValue(policy, fieldKey, checked)}
          />
        );
      case "Integer":
        return (
          <InputNumber
            style={{ width: "100%" }}
            value={typeof value === "number" ? value : undefined}
            disabled={disabled}
            min={rule.min ?? undefined}
            max={rule.max ?? undefined}
            precision={0}
            onChange={(next) =>
              updateDirtyValue(
                policy,
                fieldKey,
                typeof next === "number" ? next : null,
              )
            }
          />
        );
      case "DateTime":
        return (
          <DatePicker
            showTime
            style={{ width: "100%" }}
            value={parseDateValue(value)}
            disabled={disabled}
            onChange={(nextDate) =>
              updateDirtyValue(
                policy,
                fieldKey,
                nextDate ? nextDate.toISOString() : null,
              )
            }
          />
        );
      default:
        return (
          <Input
            placeholder={rule.description}
            value={typeof value === "string" ? value : ""}
            disabled={disabled}
            onChange={(event) =>
              updateDirtyValue(policy, fieldKey, event.target.value)
            }
          />
        );
    }
  };

  const renderPolicySection = (policy: PolicyResponse) => {
    const policyDirtyCount = Object.keys(
      dirtyValuesByPolicy[policy.policyKey] ?? {},
    ).length;

    return (
      <Card
        key={policy.policyKey}
        size="default"
        style={{
          marginBottom: 18,
          borderRadius: 16,
          borderColor: token.colorBorderSecondary,
          boxShadow: "0 10px 32px rgba(18, 34, 62, 0.06)",
          overflow: "hidden",
          transition: "all 0.2s ease",
        }}
        title={
          <Space size={10}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 8,
                background:
                  "linear-gradient(145deg, rgba(38, 85, 255, 0.16), rgba(24, 144, 255, 0.08))",
                color: token.colorPrimary,
              }}
            >
              {POLICY_ICONS[policy.policyKey] ?? <ListChecks size={16} />}
            </span>
            <Space direction="vertical" size={0}>
              <Typography.Text strong style={{ fontSize: 16 }}>
                {POLICY_LABELS[policy.policyKey] ?? policy.policyKey}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {policy.policyKey}
              </Typography.Text>
            </Space>
          </Space>
        }
        extra={
          <Space>
            {policyDirtyCount > 0 && (
              <Tag color="processing" style={{ borderRadius: 999 }}>
                {policyDirtyCount} unsaved
              </Tag>
            )}
            {!isReadOnly && (
              <Button
                type="primary"
                disabled={policyDirtyCount === 0}
                loading={savingPolicyKey === policy.policyKey}
                onClick={() => savePolicySection(policy.policyKey)}
                style={{ borderRadius: 10 }}
              >
                Save
              </Button>
            )}
          </Space>
        }
      >
        <Row gutter={[16, 12]}>
          {getRenderableFieldKeys(policy).map((fieldKey) => {
            const rule = resolveRuleForField(policy, fieldKey);

            return (
              <Col xs={24} md={12} key={`${policy.policyKey}-${fieldKey}`}>
                <div
                  style={{
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: 12,
                    padding: 14,
                    background:
                      rule.modifiable && !isReadOnly
                        ? token.colorFillAlter
                        : token.colorBgContainerDisabled,
                  }}
                >
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                    align="start"
                  >
                    <Space direction="vertical" size={1}>
                      <Typography.Text strong>
                        {formatFieldLabel(fieldKey)}
                      </Typography.Text>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        {rule.description}
                      </Typography.Text>
                    </Space>
                    <Space size={6} wrap>
                      <Tag style={{ marginInlineEnd: 0, borderRadius: 999 }}>
                        {rule.fieldType}
                      </Tag>
                      <Tag
                        style={{ marginInlineEnd: 0, borderRadius: 999 }}
                        color={
                          rule.modifiable && !isReadOnly ? "green" : "default"
                        }
                        icon={
                          rule.modifiable && !isReadOnly ? (
                            <UnlockOutlined />
                          ) : (
                            <LockOutlined />
                          )
                        }
                      >
                        {rule.modifiable && !isReadOnly
                          ? "Editable"
                          : "Read only"}
                      </Tag>
                    </Space>
                  </Space>

                  <div style={{ marginTop: 12 }}>
                    {renderField(policy, fieldKey, rule)}
                  </div>

                  <Space
                    direction="vertical"
                    size={0}
                    style={{ marginTop: 10 }}
                  >
                    {typeof rule.min === "number" ||
                    typeof rule.max === "number" ? (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        Range: {rule.min ?? "-infinity"} to{" "}
                        {rule.max ?? "infinity"}
                      </Typography.Text>
                    ) : null}
                    {policy.resolvedFrom?.[fieldKey] && (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        Resolved from: {policy.resolvedFrom[fieldKey]}
                      </Typography.Text>
                    )}
                  </Space>
                </div>
              </Col>
            );
          })}
        </Row>
      </Card>
    );
  };

  return (
    <Spin spinning={loading}>
      <div
        style={{
          margin: "18px 6px",
          padding: 16,
          borderRadius: 18,
          background:
            "radial-gradient(120% 120% at 0% 0%, rgba(24, 144, 255, 0.15), rgba(255,255,255,0.9))",
          border: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Space
          style={{ width: "100%", justifyContent: "space-between" }}
          align="start"
          wrap
        >
          <Space direction="vertical" size={2}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Organization Policies
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Configure behavior using policy metadata, including lock state and
              validation constraints.
            </Typography.Paragraph>
          </Space>

          <Space size={8} wrap>
            <Tag color="blue" style={{ borderRadius: 999, paddingInline: 10 }}>
              {policyStats.sectionCount} sections
            </Tag>
            <Tag
              color="purple"
              style={{ borderRadius: 999, paddingInline: 10 }}
            >
              {policyStats.fieldCount} fields
            </Tag>
            <Tag
              color={policyStats.dirtyFieldCount > 0 ? "gold" : "default"}
              style={{ borderRadius: 999, paddingInline: 10 }}
            >
              {policyStats.dirtyFieldCount} unsaved changes
            </Tag>
          </Space>
        </Space>
      </div>

      <Form layout="vertical" style={{ margin: "0 8px 24px" }} form={form}>
        {isReadOnly && (
          <Alert
            type="warning"
            showIcon
            message="Read-only mode"
            description="You can view policies, but editing is disabled for your role."
            style={{ marginBottom: 16, borderRadius: 12 }}
          />
        )}

        <Divider style={{ marginTop: 8 }} />

        {policies.length === 0 ? (
          <Empty description="No policy sections available." />
        ) : (
          policies.map(renderPolicySection)
        )}
      </Form>
    </Spin>
  );
};

export default CorpMetadataForm;
